import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { EMPTY, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SeFeFormFieldMultiselectComponent } from 'se-fe-form-field-multiselect';
import { SeFeFormFieldConfig } from 'se-fe-form-field';
import { SeFeFormService } from 'se-fe-form-service';
import type { SeFeOverlay } from 'se-fe-overlay';
import { ItemVariation } from 'src/shared/models/item-variation.model';
import { ChildSaleService } from '../child-sale.service';
import { catchError, finalize, take, tap } from 'rxjs/operators';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { isIntegerValidator } from 'src/app/validators/is-integer.validator';

export type ItemSearchStatus = 'idle' | 'loading' | 'results' | 'empty' | 'invalid' | 'error';

const MODAL_MULTISELECT_OVERLAY_Z_INDEX = '100001';

type ItemSearchMultiselect = SeFeFormFieldMultiselectComponent & {
  overlay?: Pick<SeFeOverlay, 'contentElement'>;
};

export function getMultiselectOverlayContent(
  itemSearchSelector?: SeFeFormFieldMultiselectComponent | null
): HTMLElement | undefined {
  return (itemSearchSelector as ItemSearchMultiselect | undefined | null)?.overlay?.contentElement;
}

@Component({
  selector: 'app-modal-base',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: './modal-base.component.html',
})
export class ModalBaseComponent implements OnInit, OnDestroy {
  @ViewChild('modalRef', { static: true }) modalRef: TemplateRef<any>;
  @ViewChild('scrollRef', { static: true }) scrollRef: ElementRef; // provides a location to scroll to
  @ViewChild('itemSelector') itemSelector: SeFeFormFieldMultiselectComponent;
  @ViewChild('creditReason') creditReasonSelector?: SeFeFormFieldMultiselectComponent;
  @Output() close = new EventEmitter<boolean>();
  @Output() submit = new EventEmitter<any>();
  @Input() size = 'medium';
  @Input() scroll?: Subject<void>;
  @Input() parent_sale_id: number;
  @Input() salesperson_full_name: string;
  @Input() sales_number: string;
  @Input() org_id: string;
  completed: number = 0;
  total: number = 2;
  step: number = 1;
  error = false;
  disabled = true;
  itemError = true;
  submitIsInProgress: boolean = false;
  public searchItems: ItemVariation[] = [];
  public searchTerm: string = '';
  public itemSearchStatus: ItemSearchStatus = 'idle';
  public items: ItemVariation[] = [];
  public filteredOptions: any = { sections: [] };
  public form: FormGroup;
  public selectedItems: FormArray;
  public multiselectForm: FormGroup;
  public readonly itemSearchMinLength = 3;
  public queryValidators = [Validators.maxLength(300)];
  public style = '690px';
  public queryConfig: SeFeFormFieldConfig = {
    label: '',
    errorMessages: {
      maxlength: 'Max length exceeded.',
    },
  };
  private modalElement?: HTMLElement;
  private modalView?: EmbeddedViewRef<any>;
  private itemSearchSelector?: SeFeFormFieldMultiselectComponent;
  private itemSearchRequestPending = false;
  private latestItemSearchRequestId = 0;
  private latestDefaultItemSearchRequestId = 0;
  private itemSearchDefaultsLoaded = false;
  private loadedItemSearchItems: ItemVariation[] = [];
  private displayedItemSearchItems: ItemVariation[] = [];
  private emptyItemSearchRestoreTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly onMultiselectOverlayOpen = (event: Event): void => {
    const overlayEvent = event as CustomEvent<{ element?: HTMLElement }>;
    const multiselectSelectors = [this.itemSearchSelector ?? this.itemSelector, this.creditReasonSelector];

    for (const multiselectSelector of multiselectSelectors) {
      if (this.getDropdownRoot(multiselectSelector) === overlayEvent.detail?.element) {
        this.elevateMultiselectOverlay(multiselectSelector);
        return;
      }
    }
  };
  private readonly onItemSearchClick = (event: MouseEvent): void => {
    if (this.itemSearchStatus !== 'loading') {
      return;
    }

    const target = event.target as Element | null;
    const itemSearchSelector = this.itemSearchSelector ?? this.itemSelector;
    const dropdownRoot = this.getDropdownRoot(itemSearchSelector);
    if (!dropdownRoot?.contains(target) || !target?.closest('[role="option"]')) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  };
  private readonly onItemSearchKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter') {
      return;
    }

    const target = event.target as Element | null;
    const itemSelector = target?.closest('se-fe-form-field-multiselect[data-item-search]');
    const itemSearchSelector = this.itemSearchSelector ?? this.itemSelector;
    const query = itemSelector?.querySelector('input')?.value;
    const dropdownRoot = this.getDropdownRoot(itemSearchSelector) ?? itemSelector;
    const hasSelectableOptions = !!dropdownRoot?.querySelector('[role="option"]');
    const normalizedQuery = query?.trim() ?? '';
    const isDebouncePending = normalizedQuery.length >= this.itemSearchMinLength && normalizedQuery !== this.searchTerm;
    const isRequestPending = this.itemSearchRequestPending && normalizedQuery === this.searchTerm;
    const isCompletedEmptySearch =
      normalizedQuery === this.searchTerm && !this.itemSearchRequestPending && !hasSelectableOptions;

    if (!this.modalElement?.contains(target) || !normalizedQuery) {
      return;
    }

    if (isDebouncePending || isRequestPending || isCompletedEmptySearch) {
      event.preventDefault();
      // Angular still dispatches the package host listener after propagation is stopped.
      // Reclassify only this handled key so the package skips its single-select Enter branch.
      Object.defineProperty(event, 'key', { value: 'Shift' });
      return;
    }

    if (this.itemSearchStatus === 'results' && hasSelectableOptions) {
      event.preventDefault();
      // Angular still dispatches the package host listener after propagation is stopped.
      // Reclassify only this handled key so the package skips its single-select Enter branch.
      Object.defineProperty(event, 'key', { value: 'Shift' });

      const activeItem = itemSearchSelector?.listKeyManager?.activeItem?.item;
      const firstItem = this.options?.sections?.find((section) => section.options?.length)?.options[0];
      const selectedItem = activeItem ?? firstItem;
      if (selectedItem) {
        itemSearchSelector?.onSelect(selectedItem, null);
      }
    }
  };

  public options: any = {
    sections: [
      {
        options: [{}],
      },
    ],
  };

  public get itemSearchNotFoundMessageKey(): string {
    switch (this.itemSearchStatus) {
      case 'loading':
        return 'CHILD_SALE.MODAL.item_search_loading';
      case 'invalid':
        return 'CHILD_SALE.MODAL.item_search_invalid';
      case 'error':
        return 'CHILD_SALE.MODAL.item_search_error';
      default:
        return 'CHILD_SALE.MODAL.item_name_not_found';
    }
  }

  public get itemSearchContextMessageKey(): string | null {
    if (!this.loadedItemSearchItems.length) {
      return null;
    }

    switch (this.itemSearchStatus) {
      case 'loading':
        return 'CHILD_SALE.MODAL.item_search_loading_with_loaded';
      case 'invalid':
        return 'CHILD_SALE.MODAL.item_search_invalid_with_loaded';
      case 'error':
        return 'CHILD_SALE.MODAL.item_search_error_with_loaded';
      default:
        return null;
    }
  }

  public get itemSearchStatusMessageKey(): string | null {
    const contextMessageKey = this.itemSearchContextMessageKey;
    if (contextMessageKey) {
      return contextMessageKey;
    }

    switch (this.itemSearchStatus) {
      case 'loading':
        return 'CHILD_SALE.MODAL.item_search_loading';
      case 'invalid':
        return 'CHILD_SALE.MODAL.item_search_invalid';
      case 'error':
        return 'CHILD_SALE.MODAL.item_search_error';
      default:
        return null;
    }
  }

  public textareaConfig = {
    label: 'Message',
  };

  constructor(
    protected viewContainerRef: ViewContainerRef,
    protected detector: ChangeDetectorRef,
    protected translate: TranslateService,
    protected fb: FormBuilder,
    protected el: ElementRef,
    protected fs: SeFeFormService,
    protected ChildSaleService: ChildSaleService
  ) {
    window.addEventListener('keydown', this.onItemSearchKeydown, true);
    window.addEventListener('click', this.onItemSearchClick, true);
    document.addEventListener('seFeOverlay:open', this.onMultiselectOverlayOpen);
    translate.setDefaultLang('en');
    translate.use('en');
  }

  ngOnInit(): void {
    this.modalView = this.viewContainerRef.createEmbeddedView(this.modalRef);
    this.detector.detectChanges();
    this.modalElement = this.modalView.rootNodes.find((node) => node instanceof HTMLElement);
    this.scrollToTop = this.scrollToTop.bind(this);
    if (this.scroll) {
      this.scroll.subscribe(this.scrollToTop);
    }
    this.itemSearchStatus = 'loading';
    this.searchInput('', false);
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.onItemSearchKeydown, true);
    window.removeEventListener('click', this.onItemSearchClick, true);
    document.removeEventListener('seFeOverlay:open', this.onMultiselectOverlayOpen);
    this.cancelEmptyItemSearchRestore();
    if (this.scroll) {
      this.scroll.unsubscribe();
    }
  }

  //Form
  protected createForm(): void {
    this.multiselectForm = this.fb.group({
      multiselect: [],
    });

    this.form = this.fb.group({
      textarea: [],
      items: this.fb.array([]),
    });
  }

  public scrollToTop(): void {
    this.scrollRef.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    this.scrollRef.nativeElement.scrollIntoView(); // this does it for mobile
  }

  public searchInput(event: any, isUserSearch = true, itemSearchSelector?: SeFeFormFieldMultiselectComponent): void {
    if (itemSearchSelector) {
      this.itemSearchSelector = itemSearchSelector;
      this.elevateMultiselectOverlay(this.itemSearchSelector);
    }
    const query = typeof event === 'string' ? event.trim() : '';
    if (isUserSearch) {
      const searchInput = this.itemSearchSelector?.elementRef.nativeElement.querySelector('input') as HTMLInputElement | null;
      if (query && searchInput && searchInput.value.trim() !== query) {
        return;
      }
      if (!query) {
        if (this.itemSearchSelector) {
          this.itemSearchSelector.query = '';
        }
        this.scheduleLoadedItemSearchRestore();
        return;
      }
      this.cancelEmptyItemSearchRestore();
      if (this.isSelectedItemSyncQuery(query)) {
        return;
      }
      this.searchTerm = query;
      this.itemSearchRequestPending = this.searchTerm.length >= this.itemSearchMinLength;
      this.itemSearchStatus = 'loading';
    }
    this.getSearchItems(query, isUserSearch);
  }

  private scheduleLoadedItemSearchRestore(): void {
    this.cancelEmptyItemSearchRestore();
    this.emptyItemSearchRestoreTimeoutId = setTimeout(() => {
      this.emptyItemSearchRestoreTimeoutId = undefined;
      const selectedValue = this.multiselectForm?.controls['multiselect'].value;
      if (!selectedValue) {
        this.restoreLoadedItemSearch();
      }
    });
  }

  private cancelEmptyItemSearchRestore(): void {
    if (this.emptyItemSearchRestoreTimeoutId === undefined) {
      return;
    }

    clearTimeout(this.emptyItemSearchRestoreTimeoutId);
    this.emptyItemSearchRestoreTimeoutId = undefined;
  }

  private isSelectedItemSyncQuery(query: string): boolean {
    const item = this.searchItems.find(
      (searchItem) => searchItem.id.toString() === query || searchItem.full_name.trim() === query
    );
    if (!item) {
      return false;
    }

    const itemId = item.id.toString();
    const pendingSelection = this.multiselectForm?.controls['multiselect'].value === itemId;
    const attachedItems = this.form?.get('items') as FormArray | null;
    const attachedSelection = attachedItems?.controls.some((control) => control.get('item')?.value.value === itemId);
    return pendingSelection || !!attachedSelection;
  }

  private restoreLoadedItemSearch(): void {
    // The multiselect emits an empty query and then the selected value or display name
    // while synchronizing a selection. None of those events is a user-initiated search.
    this.cancelEmptyItemSearchRestore();
    this.searchTerm = '';
    this.latestItemSearchRequestId++;
    this.itemSearchRequestPending = false;
    if (!this.itemSearchDefaultsLoaded) {
      this.itemSearchStatus = 'loading';
      this.getSearchItems('', false);
      return;
    }

    this.itemSearchStatus = 'idle';
    this.showLoadedItemSearchOptions();
  }

  public retryItemSearch(): void {
    if (this.itemSearchStatus !== 'error') {
      return;
    }

    const query = this.searchTerm.trim();
    const isUserSearch = query.length >= this.itemSearchMinLength;
    if (query && !isUserSearch) {
      return;
    }

    this.itemSearchRequestPending = isUserSearch;
    this.itemSearchStatus = 'loading';
    if (isUserSearch) {
      this.itemSelector.activeQuery = true;
    }
    this.getSearchItems(query, isUserSearch);
  }

  public clearItemSearch(): void {
    const input = this.modalElement?.querySelector(
      'se-fe-form-field-multiselect[data-item-search] input'
    ) as HTMLInputElement | null;
    if (!input) {
      return;
    }

    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  }

  public getSearchItems(searchItem: string, isUserSearch = true): void {
    this.requestSearchItems(searchItem, isUserSearch, true);
  }

  protected requestSearchItems(searchItem: string, isUserSearch: boolean, addons: boolean): void {
    this.setOrgId();
    const requestId = this.startItemSearchRequest();
    if (!searchItem) {
      this.latestDefaultItemSearchRequestId = requestId;
    }

    this.ChildSaleService.getSaleItems(this.org_id, 1, 25, searchItem, addons)
      .pipe(
        take(1),
        tap((response: ApiResponse<ItemVariation[]>) => {
          this.updateItemSearchResults(response.result, requestId, searchItem);
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleItemSearchError(error, requestId);
          return EMPTY;
        }),
        finalize(() => this.completeItemSearch(searchItem, isUserSearch, requestId))
      )
      .subscribe();
  }

  protected startItemSearchRequest(): number {
    this.latestItemSearchRequestId++;
    return this.latestItemSearchRequestId;
  }

  protected updateItemSearchResults(items: ItemVariation[], requestId: number, searchItem: string): void {
    const isDefaultRequest = !searchItem;
    if (isDefaultRequest && requestId === this.latestDefaultItemSearchRequestId) {
      this.loadedItemSearchItems = [...items];
      this.itemSearchDefaultsLoaded = true;
    }

    if (requestId !== this.latestItemSearchRequestId) {
      return;
    }

    if (isDefaultRequest) {
      this.itemSearchStatus = 'idle';
      this.showLoadedItemSearchOptions();
      return;
    }

    this.mergeSearchItems(items);
    this.itemSearchStatus = items.length ? 'results' : 'empty';
    this.setDisplayedItemSearchOptions(items);
    this.restoreDetachedItemSearchOverlay();
  }

  protected handleItemSearchError(error: HttpErrorResponse, requestId: number): void {
    if (requestId !== this.latestItemSearchRequestId) {
      return;
    }

    this.itemSearchStatus = error.status === 422 ? 'invalid' : 'error';
    this.showLoadedItemSearchOptions();
  }

  protected showLoadedItemSearchOptions(): void {
    this.mergeSearchItems(this.loadedItemSearchItems);
    this.setDisplayedItemSearchOptions(this.loadedItemSearchItems);
  }

  private setDisplayedItemSearchOptions(items: ItemVariation[]): void {
    this.displayedItemSearchItems = [...items];
    this.options = this.setItems(items);
    this.filteredOptions = { ...this.options };
    if (this.modalView && !this.modalView.destroyed) {
      this.modalView.detectChanges();
    }
  }

  private restoreDetachedItemSearchOverlay(): void {
    const itemSearchSelector = this.itemSearchSelector ?? this.itemSelector;
    if (!itemSearchSelector?.isOpen || this.getDropdownRoot(itemSearchSelector)?.isConnected) {
      return;
    }

    // Clicking Retry can detach the overlay without resetting the component's open state.
    itemSearchSelector.isOpen = false;
    itemSearchSelector.toggleDropdown(new MouseEvent('click'));
  }

  private getDropdownRoot(itemSearchSelector?: SeFeFormFieldMultiselectComponent): HTMLElement | undefined {
    return getMultiselectOverlayContent(itemSearchSelector);
  }

  private elevateMultiselectOverlay(multiselectSelector?: SeFeFormFieldMultiselectComponent): void {
    this.getDropdownRoot(multiselectSelector)?.style.setProperty(
      '--se-z-overlay',
      MODAL_MULTISELECT_OVERLAY_Z_INDEX
    );
  }

  private mergeSearchItems(items: ItemVariation[]): void {
    const byId = new Map(this.searchItems.map((item) => [item.id, item]));
    items.forEach((item) => byId.set(item.id, item));
    this.searchItems = Array.from(byId.values());
  }

  protected completeItemSearch(searchItem: string, isUserSearch: boolean, requestId: number): void {
    if (requestId !== this.latestItemSearchRequestId) {
      return;
    }

    const query = typeof searchItem === 'string' ? searchItem.trim() : '';

    if (isUserSearch && query === this.searchTerm) {
      this.itemSearchRequestPending = false;
    }
  }
  public setItems(items: any): any {
    const selectedItems = this.getItems().controls.map((control) => control.value.item.value);

    const section: { text: string; value: string; quantity: number }[] = items
      .filter((item) => !selectedItems.includes(item.id.toString()))
      .map((item) => ({
        text: item.full_name,
        value: item.id.toString(),
        quantity: 1,
      }));

    const options = {
      sections: [
        {
          options: section,
        },
      ],
    };
    return options;
  }

  /**
   * Resolves tenant id for API calls.
   * - Prefer numeric @Input() org_id from the host (child-sale-element) so path prefixes like `/orders/` do not overwrite it.
   * - Otherwise parse `#/org/:id`, `#/organization:id`, pathname `/organization/:id`, or scan for `org` + numeric id.
   * - Legacy fallback: split('/')[4] when truthy (original behavior for simple URLs).
   */
  protected setOrgId(): void {
    const trimmedInput = this.org_id != null ? String(this.org_id).trim() : '';
    if (trimmedInput !== '' && /^\d+$/.test(trimmedInput)) {
      return;
    }

    const href = window.location.href;

    const hashOrg = href.match(/#\/?org\/(\d+)/);
    if (hashOrg) {
      this.org_id = hashOrg[1];
      return;
    }

    const hashOrganizationColon = href.match(/#\/?organization:(\d+)/);
    if (hashOrganizationColon) {
      this.org_id = hashOrganizationColon[1];
      return;
    }

    try {
      const pathOrganization = new URL(href).pathname.match(/\/organization\/(\d+)/);
      if (pathOrganization) {
        this.org_id = pathOrganization[1];
        return;
      }
    } catch {
      /* ignore */
    }

    const parts = href.split('/');
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i].replace(/#$/, '');
      const next = parts[i + 1];
      if (seg === 'org' && next && /^\d+$/.test(String(next))) {
        this.org_id = String(next);
        return;
      }
    }

    const fourth = parts[4];
    if (fourth) {
      const raw = fourth.toString().split('?')[0].split('#')[0];
      const fromColon = raw.match(/^organization:(\d+)$/);
      this.org_id = fromColon ? fromColon[1] : raw;
    }
  }

  protected buildOrderDetailUrl(orderId: number | string): string | null {
    this.setOrgId();
    const orgId = this.org_id != null ? String(this.org_id).trim() : '';
    if (!/^\d+$/.test(orgId)) {
      return null;
    }

    const idPart = encodeURIComponent(String(orderId));
    const href = window.location.href;
    const url = new URL(href);
    const origin = url.origin;

    // se-financial-hq-ui orders app uses HashLocationStrategy (#/organization:orgId/orderId/detail).
    const hashOrgDetail = url.hash.match(/^#\/organization:(\d+)\/(\d+)\/detail/);
    if (hashOrgDetail) {
      const routeOrg = hashOrgDetail[1];
      if (String(routeOrg) === String(orgId)) {
        return `${url.origin}${url.pathname}${url.search}#/organization:${orgId}/${idPart}/detail`;
      }
      return `${url.origin}${url.pathname}${url.search}#/organization:${routeOrg}/${idPart}/detail`;
    }

    const organizationPath = url.pathname.match(/^\/organization\/(\d+)\/?$/);
    if (organizationPath) {
      return `${origin}/org/${orgId}/hq/orders/${idPart}/detail`;
    }

    if (href.includes('/hq/orders')) {
      return `${origin}/org/${orgId}/hq/orders/${idPart}/detail`;
    }

    if (href.indexOf('/org/') !== -1) {
      const base = href.split('/org/')[0] + '/org/';
      return `${base}${orgId}/orders/${idPart}/detail`;
    }

    return `${origin}/org/${orgId}/orders/${idPart}/detail`;
  }

  protected redirectToOrderDetail(orderId: number | string): void {
    const targetUrl = this.buildOrderDetailUrl(orderId);
    if (!targetUrl) {
      return;
    }
    window.location.href = targetUrl;
  }

  public getItems(): FormArray {
    this.selectedItems = this.form.get('items') as FormArray;
    return this.selectedItems;
  }

  //Set and select Items
  public apply(): void {
    const selectedValue = this.multiselectForm.controls['multiselect'].value;
    if (!this.checkForExistingItems(selectedValue)) {
      this.itemError = false;
      this.multiselectForm.controls['multiselect'].setValue('');
      return;
    } else {
      this.itemError = true;
    }
    if (selectedValue) {
      // Find the selected item directly from searchItems instead of filtering options
      const selectedItem = this.searchItems.find((item) => item.id.toString() === selectedValue);

      if (selectedItem) {
        const itemsArray = this.form.get('items') as FormArray;

        // Check if item is already in the form
        const existingItem = itemsArray.controls.find((control) => control.get('item')?.value.value === selectedValue);

        if (existingItem) {
          this.multiselectForm.controls['multiselect'].setValue('');
          this.multiselectForm.controls['multiselect'].markAsPristine();
          return;
        }

        // Create the option object from the selected item
        const selectedOption = {
          text: selectedItem.full_name,
          value: selectedItem.id.toString(),
          quantity: 1,
        };

        const itemGroup = this.fb.group({
          item: [selectedOption],
          quantity: [1, [Validators.required, Validators.min(1), Validators.max(999), isIntegerValidator()]],
        });
        itemsArray.push(itemGroup);
        this.disabled = false;
        this.restoreLoadedItemSearch();
      }
    }
    this.multiselectForm.controls['multiselect'].setValue('');
    this.multiselectForm.controls['multiselect'].markAsPristine();
  }

  public getTotalItems(): number {
    const items = this.getItems().value;
    return items.reduce((total, item) => total + (item.quantity || 0), 0);
  }

  public removeItem(i: number): void {
    if (i >= 0 && i < this.selectedItems.length) {
      this.selectedItems.removeAt(i);
    }
    if (this.selectedItems.length === 0) {
      this.disabled = true;
    }
    if (this.searchTerm) {
      this.itemSelector.activeQuery = true;
    }
    this.setDisplayedItemSearchOptions(this.displayedItemSearchItems);
  }

  private checkForExistingItems(value: string): boolean {
    return this.searchItems.some((item) => item.id.toString() === value);
  }

  public closeForm(): void {
    this.cleanForm();
    this.close.emit(true);
  }

  public cleanForm(): void {
    this.form.reset();
    this.multiselectForm.reset();
    this.getItems().clear();
    this.selectedItems = this.form.get('items') as FormArray;
    this.items = [];
    this.step = 1;
    this.completed = 0;
    this.disabled = true;
    this.itemError = true;
    this.searchItems = [];
    this.latestItemSearchRequestId++;
    this.latestDefaultItemSearchRequestId = 0;
    this.itemSearchDefaultsLoaded = false;
    this.loadedItemSearchItems = [];
    this.displayedItemSearchItems = [];
    this.cancelEmptyItemSearchRestore();
    this.searchTerm = '';
    this.itemSearchStatus = 'idle';
    this.options = { sections: [{ options: [] }] };
    this.filteredOptions = { sections: [{ options: [] }] };
  }

  //Build order and helpers
  public itemsToOrder(): void {
    const matchingValues = this.selectedItems.value
      .map((item) => this.searchItems.find((searchItem) => searchItem.id === Number(item.item.value)))
      .filter(Boolean);
    this.items = [...matchingValues];
  }

  public getSum(price: number, quantity: number): number {
    return price * quantity;
  }

  public itemsSum(): number {
    const itemsArray = this.getItems();
    return itemsArray.controls.reduce((acc, itemGroup, index) => {
      const itemValue = itemGroup.get('item')?.value;
      const quantity = itemGroup.get('quantity')?.value;

      if (!itemValue || !quantity) {
        return acc;
      }

      const selectedItem = this.searchItems.find((searchItem) => searchItem.id === Number(itemValue.value));
      if (!selectedItem) {
        return acc;
      }

      let price = this.getPriceOrPriceVariation(selectedItem);
      return acc + price * quantity;
    }, 0);
  }

  public itemsAreInvalid() {
    return this.form.get('items').invalid;
  }

  //Modal steps
  public nextStep(): void {
    if (this.selectedItems.length > 0 && !this.form.get('items').invalid) {
      this.step++;
      this.completed++;
    }
    this.itemsToOrder();
  }

  public back(): void {
    this.completed--;
    this.step--;
  }

  protected getPriceOrPriceVariation(selectedItem: ItemVariation): number {
    return Number(selectedItem.price);
  }
}
